'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  entryMeta,
  getUserTournamentHistory,
  summarizeHistory,
  type TournamentHistoryEntry,
} from '@/lib/tournament-history';
import { EventArt, Medal, PlacePill, fill, trackHistory, useHistoryCopy } from './parts';

/** Events shown before "See all". */
const COLLAPSED_COUNT = 3;

const int = (v: number | null) => (v === null ? '–' : Math.round(v).toLocaleString('en-US'));

interface Props {
  userId: string;
  locale: string;
  /**
   * Server-fetched history. `undefined` = fetch on the client; `null` = the
   * RPC is unavailable (render nothing).
   */
  initialEntries?: TournamentHistoryEntry[] | null;
  /** Own profile: shows a small "Browse tournaments" prompt when empty. */
  isOwnProfile?: boolean;
  surface: 'own_profile' | 'profile' | 'public_profile';
  className?: string;
}

/**
 * Tournament history shown in place on the profile (inside the Apex card, or as
 * its own card): a stat strip, then the events as an accordion. The first few
 * events show, the rest open with "See all". Renders nothing when there's no
 * history, except a browse prompt on your own profile.
 */
export function TournamentHistoryInline({
  userId,
  locale,
  initialEntries,
  isOwnProfile = false,
  surface,
  className = '',
}: Props) {
  const copy = useHistoryCopy();
  const reduce = useReducedMotion();
  const [entries, setEntries] = useState<TournamentHistoryEntry[] | null | undefined>(initialEntries);
  const [expanded, setExpanded] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (initialEntries !== undefined) return;
    let cancelled = false;
    getUserTournamentHistory(userId).then((rows) => {
      if (!cancelled) setEntries(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, initialEntries]);

  const renders = !!entries && (entries.length > 0 || isOwnProfile);
  const viewedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!renders || !entries || viewedFor.current === userId) return;
    viewedFor.current = userId;
    trackHistory('tournament_history_card_viewed', {
      own: isOwnProfile,
      surface,
      played_count: entries.length,
      has_results: entries.some((e) => e.hasResults),
    });
  }, [renders, entries, userId, isOwnProfile, surface]);

  if (!entries || !renders) return null;

  const browseHref = `/${locale}/tournaments`;

  if (entries.length === 0) {
    return (
      <Link
        href={browseHref}
        onClick={() => trackHistory('tournament_cta_clicked', { surface: 'empty_state' })}
        className={`flex min-h-12 items-center gap-3 rounded-xl bg-white/[0.03] px-4 py-3 ring-1 ring-white/[0.06] transition-colors hover:bg-white/[0.06] ${className}`}
      >
        <Medal rank={null} size={28} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-white">{copy.emptyTitle}</span>
          <span className="truncate text-xs text-white/55">{copy.browse}</span>
        </span>
      </Link>
    );
  }

  const summary = summarizeHistory(entries);
  const stats = [
    { label: copy.played, value: summary.played.toLocaleString('en-US') },
    { label: copy.best, value: summary.bestRank ? `#${summary.bestRank}` : '–' },
    { label: copy.topTens, value: summary.topTens.toLocaleString('en-US') },
    { label: copy.kills, value: summary.kills.toLocaleString('en-US') },
  ];
  const visible = expanded ? entries : entries.slice(0, COLLAPSED_COUNT);
  const hidden = entries.length - COLLAPSED_COUNT;

  return (
    <div className={`overflow-hidden rounded-xl bg-white/[0.03] ring-1 ring-white/[0.06] ${className}`}>
      <div className="flex items-center gap-2 px-4 pt-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{copy.title}</p>
      </div>

      <dl className="grid grid-cols-4 px-4 pb-3 pt-2">
        {stats.map((s) => (
          <div key={s.label} className="flex min-w-0 flex-col-reverse">
            <dt className="truncate text-[10px] font-bold uppercase tracking-widest text-white/45">{s.label}</dt>
            <dd className="truncate text-lg font-semibold tabular-nums text-white">{s.value}</dd>
          </div>
        ))}
      </dl>

      <ul className="border-t border-white/[0.06]">
        {visible.map((e) => {
          const open = openId === e.tournamentId;
          const eventHref = `/${locale}/tournaments/${encodeURIComponent(e.slug)}`;
          const cells = [
            { label: e.fieldSize ? fill(copy.ofField, { count: e.fieldSize }) : copy.rank, value: e.rank ? `#${e.rank}` : '–' },
            { label: copy.kills, value: int(e.kills) },
            { label: copy.damage, value: int(e.damage) },
            { label: copy.avgPlace, value: e.avgPlacement === null ? '–' : e.avgPlacement.toFixed(1) },
            { label: copy.games, value: int(e.gamesPlayed) },
            { label: copy.team, value: e.team ?? '–' },
          ];
          return (
            <li key={e.tournamentId} className="border-b border-white/[0.06] last:border-b-0">
              <button
                type="button"
                onClick={() => {
                  if (!open) trackHistory('tournament_history_event_expanded', { has_results: e.hasResults });
                  setOpenId(open ? null : e.tournamentId);
                }}
                aria-expanded={open}
                className="flex min-h-14 w-full cursor-pointer items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-white/[0.04]"
              >
                <EventArt entry={e} size={36} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-semibold text-white">{e.title}</span>
                  <span className="truncate text-xs text-white/50">{entryMeta(e, { prize: true })}</span>
                </span>
                <PlacePill rank={e.rank} label={copy.played} />
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-white/40 ${reduce ? '' : 'transition-transform duration-200'} ${open ? 'rotate-180' : ''}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key="body"
                    initial={reduce ? false : { opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduce ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col gap-2.5 px-4 pb-4 pl-[64px]"
                  >
                    {e.hasResults ? (
                      <div className="grid grid-cols-3 gap-x-2 gap-y-2.5 sm:grid-cols-6">
                        {cells.map((c) => (
                          <div key={c.label} className="flex min-w-0 flex-col">
                            <span className="truncate text-[15px] font-semibold tabular-nums text-white">{c.value}</span>
                            <span className="truncate text-[10px] font-bold uppercase tracking-widest text-white/45">{c.label}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-white/55">{copy.notTracked}</p>
                    )}
                    <Link
                      href={e.hasResults ? `${eventHref}/results` : eventHref}
                      onClick={() => trackHistory('tournament_results_link_clicked', { has_results: e.hasResults })}
                      className="inline-flex min-h-8 items-center self-start text-xs text-white/70 underline underline-offset-4 transition-colors hover:text-white"
                    >
                      {e.hasResults ? copy.viewResults : copy.viewEvent}
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => {
            if (!expanded) trackHistory('tournament_history_opened', { own: isOwnProfile, source: 'card' });
            setExpanded(!expanded);
          }}
          aria-expanded={expanded}
          className="flex min-h-11 w-full cursor-pointer items-center justify-center gap-1.5 border-t border-white/[0.06] text-xs font-medium text-white/60 transition-colors hover:bg-white/[0.04] hover:text-white"
        >
          {expanded ? copy.showLess : `${copy.seeAll} (${entries.length})`}
          <ChevronDown size={14} className={expanded ? 'rotate-180' : ''} />
        </button>
      )}
    </div>
  );
}
